// A gentle, produced-sounding ambient pad synthesized with the Web Audio API.
// Kept intentionally soft so it sits *under* the narrator and the family's real
// voices — never replacing them. No external audio assets / licensing needed.
export class AmbientBed {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.nodes = [];
    this.lfo = null;
  }

  start() {
    if (this.ctx) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    if (this.ctx.state === "suspended") this.ctx.resume();

    this.master = this.ctx.createGain();
    this.master.gain.value = 0;

    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 720;
    filter.Q.value = 0.4;
    filter.connect(this.master);
    this.master.connect(this.ctx.destination);

    // Warm, open chord (C major, spread across octaves) — soft and unobtrusive.
    const freqs = [130.81, 196.0, 329.63, 523.25];
    freqs.forEach((f, i) => {
      const o = this.ctx.createOscillator();
      o.type = i % 2 ? "sine" : "triangle";
      o.frequency.value = f;
      o.detune.value = (i - 1.5) * 4; // slight detune = gentle chorus warmth
      const g = this.ctx.createGain();
      g.gain.value = 0.22 / (i * 0.5 + 1);
      o.connect(g);
      g.connect(filter);
      o.start();
      this.nodes.push(o, g);
    });

    // Slow "breathing" swell so the bed feels alive, like a produced record.
    this.lfo = this.ctx.createOscillator();
    this.lfo.frequency.value = 0.08;
    const lg = this.ctx.createGain();
    lg.gain.value = 0.018;
    this.lfo.connect(lg);
    lg.connect(this.master.gain);
    this.lfo.start();

    this._level(0.07);
  }

  _level(v) {
    if (!this.ctx) return;
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.9);
  }

  // Louder under narration, quieter (ducked) under real voices.
  duck(on) {
    this._level(on ? 0.03 : 0.08);
  }

  stop() {
    if (!this.ctx) return;
    this._level(0);
    const ctx = this.ctx;
    setTimeout(() => { try { ctx.close(); } catch (e) { /* noop */ } }, 1200);
    this.ctx = null;
    this.nodes = [];
    this.lfo = null;
  }
}
